import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { updateTicketAssignee } from 'flow/actions/tickets-action';
import EmojiSearch from 'flow/components/messages/EmojiSearch';
import useAttachmentHandler from 'flow/components/tickets/UseAttachmentHandler';
import useAppContext from 'flow/hooks/useAppContext';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import { featureFlagCheck } from 'flow/utils/feature-flag-utils';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { selectFeatureFlags } from 'flow/selectors/feature-flag-selectors';
import { selectEmailSignaturesEnabled, selectAssignee, selectHasAttachmentRestrictionsInStore, selectOriginDTOSubtype, selectTicketReplyDraft, selectTicketReplyDraftMentions, selectTicketsAllowedMimeTypesData, selectTicketsBlockedMimeTypes, selectWhatsAppEventsAreFetched, makeSelectRequiresWhatsAppTemplateReply } from 'flow/selectors/TicketsSelectors';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { twMerge } from 'tailwind-merge';
import Skeleton from 'flow/components/ui-components/Skeleton';
import { fetchTicketAttachmentRestrictions } from 'flow/components/tickets/Input/InputActions';
import AttachmentButton from 'flow/components/tickets/Input/AttachmentButton';
import EmojiPickerTickets from 'flow/components/tickets/Input/EmojiPickerTickets';
import InputFooterV2 from './InputFooterV2';
import TicketInputAttachmentPreview from 'flow/components/tickets/Input/TicketInputAttachmentPreview';
import InputLockIndicator from 'flow/components/tickets/Input/InputLockIndicator';
import { getPossibleMentionString } from 'flow/components/tickets/Input/MentionPicker/MentionPicker';
import TicketMentionPicker from 'flow/components/tickets/Input/MentionPicker/TicketMentionPicker';
import { useExternalMessagePlaceholder } from 'flow/components/tickets/Input/hooks/useExternalMessagePlaceholder';
import { useSendTicketMessage } from 'flow/components/tickets/Input/hooks/useSendTicketMessage';
import { useTicketInputKeyboard } from 'flow/components/tickets/Input/hooks/useTicketInputKeyboard';
import { useTicketMessageContent } from 'flow/components/tickets/Input/hooks/useTicketMessageContent';
import { useTicketComposerTab } from 'flow/components/tickets/Input/hooks/useTicketComposerTab';
import { getChannelSelected, replyChannelSupportsAttachments, useExternalReplyChannel } from 'flow/components/tickets/Input/hooks/useExternalReplyChannel';
import { useTextChangeTracking } from 'flow/components/tickets/Input/hooks/useTextChangeTracking';
import { useMentionAutoComplete } from 'flow/components/tickets/Input/hooks/useMentionAutoComplete';
import { useInputLockState } from 'flow/components/tickets/Input/hooks/useInputLockState';
import { useInputFocus } from 'flow/components/tickets/Input/hooks/useInputFocus';
import BannerEmailRecipient from 'flow/components/tickets/Input/Email/BannerEmailRecipient';
import { useDefaultPremadeAnswer } from 'flow/hooks/api/usePremadeAnswer';
import EmailSignaturePreview from './EmailSignaturePreview';
import { shouldApplyDefaultSignature } from './email-signature-utils';
import useTicketAction from 'flow/hooks/useTicketAction';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// While a WhatsApp ticket's events are loading its reply state (whether a
// template reply is required) is unknown, so we keep the composer disabled and
// blank the placeholder -- the skeleton is shown in its place instead of a
// possibly-misleading prompt.
var getComposerInputState = function getComposerInputState(_ref) {
  var whatsAppEventsLoading = _ref.whatsAppEventsLoading,
    disabled = _ref.disabled,
    tabSelected = _ref.tabSelected,
    tabSelectedIsWritable = _ref.tabSelectedIsWritable,
    externalPlaceholder = _ref.externalPlaceholder,
    internalPlaceholder = _ref.internalPlaceholder;
  var inputDisabled = disabled || whatsAppEventsLoading ||
  // The tab the viewer is on may be one the backend refuses: a member
  // without the tickets licence may neither reply nor write a note.
  !tabSelectedIsWritable;
  var activePlaceholder = tabSelected === 'reply_external' ? externalPlaceholder : internalPlaceholder;
  var placeholder = whatsAppEventsLoading ? '' : activePlaceholder;

  // activePlaceholder as well: the name has to hold while the placeholder is
  // blanked, or the field loses it for exactly as long as the ticket loads.
  return {
    inputDisabled: inputDisabled,
    placeholder: placeholder,
    activePlaceholder: activePlaceholder
  };
};

// Overlays the whole input row (matching its background) so the empty, disabled
// textarea and the attachment button are hidden behind a single skeleton bar
// while a WhatsApp ticket's events are still loading.
var ComposerLoadingPlaceholder = function ComposerLoadingPlaceholder(_ref2) {
  var loading = _ref2.loading;
  if (!loading) return null;
  return /*#__PURE__*/_jsx("div", {
    className: "pointer-events-none absolute inset-0 flex items-center rounded-sm bg-gray-200 px-3",
    children: /*#__PURE__*/_jsx(Skeleton.Text, {
      className: "h-3.5 w-40 text-gray-300"
    })
  });
};
var MessageInputTicketV2 = function MessageInputTicketV2(_ref3) {
  var ticketKey = _ref3.ticketKey,
    _ref3$disabled = _ref3.disabled,
    disabled = _ref3$disabled === void 0 ? false : _ref3$disabled,
    _ref3$autoFocus = _ref3.autoFocus,
    autoFocus = _ref3$autoFocus === void 0 ? true : _ref3$autoFocus;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var runTicketAction = useTicketAction();
  var webclientFeatureFlags = useAppSelector(selectFeatureFlags);
  var userAllowedToSendAttachments = useMemo(function () {
    return featureFlagCheck(webclientFeatureFlags, FeatureFlags.SEND_TICKET_ATTACHMENT);
  }, [webclientFeatureFlags]);
  var replyExternalDraft = useAppSelector(function (state) {
    return selectTicketReplyDraft(state, ticketKey, 'external');
  });
  var internalDraft = useAppSelector(function (state) {
    return selectTicketReplyDraft(state, ticketKey, 'internal');
  });
  var internalDraftMentions = useAppSelector(function (state) {
    return selectTicketReplyDraftMentions(state, ticketKey, 'internal');
  });
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var hasAttachmentRestrictionsInStore = useAppSelector(selectHasAttachmentRestrictionsInStore);
  useEffect(function () {
    if (hasAttachmentRestrictionsInStore) return;
    void dispatch(fetchTicketAttachmentRestrictions());
  }, [dispatch, hasAttachmentRestrictionsInStore]);
  var originSubtype = useAppSelector(function (state) {
    return selectOriginDTOSubtype(state, ticketKey);
  });
  var whatsAppEventsAreFetched = useAppSelector(function (state) {
    return selectWhatsAppEventsAreFetched(state, ticketKey);
  });
  var whatsAppEventsLoading = !whatsAppEventsAreFetched;
  var assignee = useAppSelector(function (state) {
    return selectAssignee(state, ticketKey);
  });
  var _useExternalReplyChan = useExternalReplyChannel(ticketKey, originSubtype),
    externalChannelSelected = _useExternalReplyChan.externalChannelSelected,
    setExternalChannelSelected = _useExternalReplyChan.setExternalChannelSelected;
  var selectRequiresWhatsAppTemplateReply = useMemo(makeSelectRequiresWhatsAppTemplateReply, []);
  var whatsAppTemplateReplyRequired = useAppSelector(function (state) {
    return selectRequiresWhatsAppTemplateReply(state, ticketKey);
  });
  var inputFieldRef = useRef(null);
  var mentionPickerRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showMentionPicker = _useState2[0],
    setShowMentionPicker = _useState2[1];
  var _useState3 = useState(assignee === ownExtensionKey && !whatsAppTemplateReplyRequired),
    _useState4 = _slicedToArray(_useState3, 2),
    allowInputInExternalReply = _useState4[0],
    setAllowInputInExternalReply = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    unlockButtonClicked = _useState6[0],
    setUnlockButtonClicked = _useState6[1];
  var _useTicketMessageCont = useTicketMessageContent({
      ticketKey: ticketKey,
      internalDraft: internalDraft,
      internalDraftMentions: internalDraftMentions,
      replyExternalDraft: replyExternalDraft,
      inputFieldRef: inputFieldRef
    }),
    messageContentInternal = _useTicketMessageCont.messageContentInternal,
    setMessageContentInternal = _useTicketMessageCont.setMessageContentInternal,
    messageContentReplyExternal = _useTicketMessageCont.messageContentReplyExternal,
    setMessageContentReplyExternal = _useTicketMessageCont.setMessageContentReplyExternal,
    updateMessageAndCaretPosition = _useTicketMessageCont.updateMessageAndCaretPosition;
  var _useState7 = useState(inputFieldRef === null || inputFieldRef === void 0 ? void 0 : inputFieldRef.current),
    _useState8 = _slicedToArray(_useState7, 2),
    textField = _useState8[0],
    setTextField = _useState8[1];
  var _useTicketComposerTab = useTicketComposerTab({
      ticketKey: ticketKey,
      originSubtype: originSubtype
    }),
    tabSelected = _useTicketComposerTab.tabSelected,
    setTabSelected = _useTicketComposerTab.setTabSelected,
    tabSelectedIsWritable = _useTicketComposerTab.tabSelectedIsWritable,
    canReply = _useTicketComposerTab.canReply,
    canAddInternalNote = _useTicketComposerTab.canAddInternalNote,
    originPending = _useTicketComposerTab.originPending,
    replyUnsupported = _useTicketComposerTab.replyUnsupported;
  var channelSelected = getChannelSelected(tabSelected, externalChannelSelected);
  var activeMessageContent = tabSelected === 'reply_external' ? messageContentReplyExternal : messageContentInternal;
  var attachmentButtonDisabled = !tabSelectedIsWritable || tabSelected === 'reply_external' && !allowInputInExternalReply;
  var channelSupportsAttachments = tabSelected === 'internal' || replyChannelSupportsAttachments(externalChannelSelected);
  var allowedAttachmentsData = useAppSelector(function (state) {
    return selectTicketsAllowedMimeTypesData(state, channelSelected);
  });
  var blockedAttachmentsMimeTypes = useAppSelector(function (state) {
    return selectTicketsBlockedMimeTypes(state, channelSelected);
  });
  var _useState9 = useState(new Set()),
    _useState0 = _slicedToArray(_useState9, 2),
    sentSegmentTracks = _useState0[0],
    setSentSegmentTracks = _useState0[1];
  var resetSegmentTrackingFlags = useCallback(function (channel) {
    setSentSegmentTracks(function (prev) {
      var next = new Set(prev);
      next["delete"](channel);
      return next;
    });
  }, []);
  var _useTextChangeTrackin = useTextChangeTracking({
      sentSegmentTracks: sentSegmentTracks,
      setSentSegmentTracks: setSentSegmentTracks
    }),
    trackTextChangeStart = _useTextChangeTrackin.trackTextChangeStart;
  var _useSendTicketMessage = useSendTicketMessage({
      ticketKey: ticketKey,
      tabSelectedIsWritable: tabSelectedIsWritable,
      messageContentInternal: messageContentInternal,
      messageContentReplyExternal: messageContentReplyExternal,
      setMessageContentInternal: setMessageContentInternal,
      setMessageContentReplyExternal: setMessageContentReplyExternal,
      resetSegmentTrackingFlags: resetSegmentTrackingFlags
    }),
    handleSendMessage = _useSendTicketMessage.handleSendMessage;
  var showWhatsAppInactivityTemplateState = whatsAppTemplateReplyRequired && (assignee === ownExtensionKey || unlockButtonClicked);
  var externalMessagePlaceholder = useExternalMessagePlaceholder({
    ticketKey: ticketKey,
    externalChannelSelected: externalChannelSelected,
    allowInputInExternalReply: allowInputInExternalReply,
    unlockButtonClicked: unlockButtonClicked
  });
  var _getComposerInputStat = getComposerInputState({
      whatsAppEventsLoading: whatsAppEventsLoading,
      disabled: disabled,
      tabSelected: tabSelected,
      tabSelectedIsWritable: tabSelectedIsWritable,
      externalPlaceholder: externalMessagePlaceholder,
      internalPlaceholder: i18next.t('ticket.note_internal.write')
    }),
    inputDisabled = _getComposerInputStat.inputDisabled,
    placeholder = _getComposerInputStat.placeholder,
    activePlaceholder = _getComposerInputStat.activePlaceholder;

  // Feed the displayed placeholder (blank while a WhatsApp ticket loads, real
  // text after) so the height is re-measured once the textarea becomes enabled
  // -- otherwise the height measured during loading sticks and clips the
  // placeholder.
  useAutosizeTextArea(inputFieldRef, (activeMessageContent === null || activeMessageContent === void 0 ? void 0 : activeMessageContent.text) || placeholder);
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isChatSwitched = _useState10[0],
    setIsChatSwitched = _useState10[1];
  useEffect(function () {
    if (assignee !== ownExtensionKey || whatsAppTemplateReplyRequired) {
      setAllowInputInExternalReply(false);
    } else {
      setAllowInputInExternalReply(true);
    }
  }, [assignee, ownExtensionKey, whatsAppTemplateReplyRequired]);
  useInputFocus({
    inputFieldRef: inputFieldRef,
    tabSelected: tabSelected,
    allowInputInExternalReply: allowInputInExternalReply,
    isChatSwitched: isChatSwitched,
    setIsChatSwitched: setIsChatSwitched,
    ticketKey: ticketKey,
    autoFocus: autoFocus
  });
  var closeMentionPicker = useCallback(function () {
    setShowMentionPicker(false);
  }, []);
  useEffect(function () {
    var _textField$selectionS;
    if (tabSelected !== 'internal') return;
    var text = (messageContentInternal === null || messageContentInternal === void 0 ? void 0 : messageContentInternal.text) || '';
    var textBeforeCaret = text.slice(0, (_textField$selectionS = textField === null || textField === void 0 ? void 0 : textField.selectionStart) !== null && _textField$selectionS !== void 0 ? _textField$selectionS : 0);
    if (getPossibleMentionString(textBeforeCaret)) {
      setShowMentionPicker(true);
    } else {
      setShowMentionPicker(false);
    }
  }, [messageContentInternal === null || messageContentInternal === void 0 ? void 0 : messageContentInternal.text, tabSelected, textField === null || textField === void 0 ? void 0 : textField.selectionStart]);
  var getCaretPosition = function getCaretPosition() {
    return textField === null || textField === void 0 ? void 0 : textField.selectionStart;
  };
  var setCaretPosition = function setCaretPosition(position) {
    setMessageFieldFocus();

    // We need to set caret position and force a re-render in the next event loop since the call to focus
    // above sets the caret already. Without the timeout, caret position would always be set at end of text.
    setTimeout(function () {
      if (textField && textField.setSelectionRange) {
        textField.setSelectionRange(position, position);
      }
    });
  };
  var getPossibleMentionText = function getPossibleMentionText() {
    var _messageContentIntern;
    var textTrimmedToCaret = (_messageContentIntern = messageContentInternal.text) === null || _messageContentIntern === void 0 ? void 0 : _messageContentIntern.substring(0, getCaretPosition());
    return getPossibleMentionString(textTrimmedToCaret);
  };
  var handleTextChanged = function handleTextChanged(event) {
    // Early return for external reply if not allowed
    if (tabSelected === 'reply_external' && !allowInputInExternalReply) {
      return;
    }
    var text = event.target.value;
    var caretPosition = event.target.selectionStart;
    updateMessageAndCaretPosition(text, caretPosition, tabSelected);
    setTextField(event.target);

    // Track user began writing
    trackTextChangeStart(channelSelected);
  };
  var onClickTextField = function onClickTextField(/*event: React.MouseEvent<HTMLTextAreaElement>*/
  ) {
    var _inputFieldRef$curren;
    setMessageFieldFocus();
    if (whatsAppTemplateReplyRequired) inputFieldRef === null || inputFieldRef === void 0 || (_inputFieldRef$curren = inputFieldRef.current) === null || _inputFieldRef$curren === void 0 || _inputFieldRef$curren.blur();
    if (tabSelected === 'reply_external' && !allowInputInExternalReply) {
      if (assignee === 'unassigned') {
        runTicketAction(updateTicketAssignee(ticketKey, ownExtensionKey));
        dispatch(segmentTrack(TICKET_STRINGS.ASSIGN_TO_SELF_AND_REPLY_INPUT_CLICK, {
          component: 'Ticket input field'
        }));
      } else {
        var _inputFieldRef$curren2;
        inputFieldRef === null || inputFieldRef === void 0 || (_inputFieldRef$curren2 = inputFieldRef.current) === null || _inputFieldRef$curren2 === void 0 || _inputFieldRef$curren2.blur();
        return;
      }
    }
  };
  var setMessageFieldFocus = useCallback(function () {
    var _inputFieldRef$curren3;
    if (tabSelected === 'reply_external' && !allowInputInExternalReply) return;
    (_inputFieldRef$curren3 = inputFieldRef.current) === null || _inputFieldRef$curren3 === void 0 || _inputFieldRef$curren3.focus();
  }, [tabSelected, allowInputInExternalReply, inputFieldRef]);
  var onClickTab = useCallback(function (tab) {
    setTabSelected(tab.key);
    setTextField(inputFieldRef.current);
    setMessageFieldFocus();
  }, [setMessageFieldFocus]);
  var sendMessage = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 1;
            return handleSendMessage(channelSelected);
          case 1:
            setMessageFieldFocus();
          case 2:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function sendMessage() {
      return _ref4.apply(this, arguments);
    };
  }();
  var _useTicketInputKeyboa = useTicketInputKeyboard({
      showMentionPicker: showMentionPicker,
      setShowMentionPicker: setShowMentionPicker,
      mentionPickerRef: mentionPickerRef,
      tabSelected: tabSelected,
      messageContentInternal: messageContentInternal,
      messageContentReplyExternal: messageContentReplyExternal,
      sendMessage: sendMessage
    }),
    handleKeyDown = _useTicketInputKeyboa.handleKeyDown;
  var _useMentionAutoComple = useMentionAutoComplete({
      messageContentInternal: messageContentInternal,
      setMessageContentInternal: setMessageContentInternal,
      getCaretPosition: getCaretPosition,
      setCaretPosition: setCaretPosition,
      setShowMentionPicker: setShowMentionPicker,
      sendMessage: sendMessage
    }),
    handleMentionAutoComplete = _useMentionAutoComple.handleMentionAutoComplete;
  var onClickUnlockButton = function onClickUnlockButton(event) {
    dispatch(segmentTrack(TICKET_STRINGS.CLICKED_UNLOCK_TO_REPLY, {
      component: 'Ticket input field'
    }));
    event.preventDefault();
    // preventDefault required or sometimes a '?' is added to the browser path, causing the page to reload.
    // This because the button is inside a form. Something with submit action being triggered..
    setUnlockButtonClicked(true);
    if (!whatsAppTemplateReplyRequired) {
      setAllowInputInExternalReply(true);
    }
  };
  var inputLockState = useInputLockState({
    tabSelected: tabSelected,
    allowInputInExternalReply: allowInputInExternalReply,
    assignee: assignee,
    showWhatsAppInactivityTemplateState: showWhatsAppInactivityTemplateState,
    ownExtensionKey: ownExtensionKey
  });
  var _useAttachmentHandler = useAttachmentHandler({
      setMessageContentInternal: setMessageContentInternal,
      setMessageContentReplyExternal: setMessageContentReplyExternal,
      tabSelected: tabSelected,
      allowInputInExternalReply: allowInputInExternalReply,
      externalChannel: externalChannelSelected,
      userAllowedToSendAttachments: userAllowedToSendAttachments,
      allowedAttachmentsData: allowedAttachmentsData,
      blockedMimeTypes: blockedAttachmentsMimeTypes
    }),
    fileInputRef = _useAttachmentHandler.fileInputRef,
    handleDrop = _useAttachmentHandler.handleDrop,
    handleAddAttachment = _useAttachmentHandler.handleAddAttachment,
    handleRemoveAttachment = _useAttachmentHandler.handleRemoveAttachment,
    handlePasteImage = _useAttachmentHandler.handlePasteImage,
    onAttachmentAspectRatioFound = _useAttachmentHandler.onAttachmentAspectRatioFound;
  var shouldDisplayEmailBanner = useMemo(function () {
    return originSubtype === 'email' && tabSelected === 'reply_external' && allowInputInExternalReply;
  }, [originSubtype, tabSelected, allowInputInExternalReply]);
  var emailSignaturesEnabled = useAppSelector(selectEmailSignaturesEnabled);
  var defaultPremadeAnswer = useDefaultPremadeAnswer();
  useEffect(function () {
    if (shouldApplyDefaultSignature({
      emailSignaturesEnabled: emailSignaturesEnabled,
      tabSelected: tabSelected,
      originSubtype: originSubtype,
      defaultSignature: defaultPremadeAnswer === null || defaultPremadeAnswer === void 0 ? void 0 : defaultPremadeAnswer.message,
      messageContent: {
        emailSignature: messageContentReplyExternal.emailSignature,
        emailSignatureDismissed: messageContentReplyExternal.emailSignatureDismissed
      }
    })) {
      setMessageContentReplyExternal(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          emailSignature: defaultPremadeAnswer === null || defaultPremadeAnswer === void 0 ? void 0 : defaultPremadeAnswer.message
        });
      });
    }
    // The two signature fields rather than the message object: the object
    // is new on every keystroke, which would run this on each one.
  }, [emailSignaturesEnabled, tabSelected, originSubtype, defaultPremadeAnswer, setMessageContentReplyExternal, messageContentReplyExternal.emailSignature, messageContentReplyExternal.emailSignatureDismissed]);
  var handleSelectPremadeAnswer = useCallback(function (answer) {
    setMessageContentReplyExternal(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        emailSignature: answer.message,
        emailSignatureDismissed: false
      });
    });
  }, [setMessageContentReplyExternal]);
  var handleRemoveSignature = useCallback(function () {
    var _inputFieldRef$curren4;
    setMessageContentReplyExternal(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        emailSignature: undefined,
        emailSignatureDismissed: true
      });
    });
    // The button removes itself along with the signature, so focus would
    // otherwise fall to <body>. The reply being written is the nearest
    // thing to carry on from.
    (_inputFieldRef$curren4 = inputFieldRef.current) === null || _inputFieldRef$curren4 === void 0 || _inputFieldRef$curren4.focus();
  }, [setMessageContentReplyExternal]);
  var isEmailExternalReply = originSubtype === 'email' && tabSelected === 'reply_external';
  return /*#__PURE__*/_jsxs("div", {
    className: "flex w-full flex-col",
    onDrop: function onDrop(event) {
      if (tabSelected === 'reply_external' && !allowInputInExternalReply) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        handleDrop(event);
      }
    },
    children: [/*#__PURE__*/_jsx(TicketInputAttachmentPreview, {
      attachment: activeMessageContent.attachment,
      imageSrc: activeMessageContent.imageSrc,
      onClickRemove: handleRemoveAttachment,
      onAspectRatioFound: function onAspectRatioFound(aspectRatio) {
        return onAttachmentAspectRatioFound(aspectRatio, tabSelected);
      }
    }), /*#__PURE__*/_jsxs("div", {
      onClick: setMessageFieldFocus,
      children: [showMentionPicker ? /*#__PURE__*/_jsx(TicketMentionPicker, {
        ticketKey: ticketKey,
        text: getPossibleMentionText(),
        onCloseRequested: closeMentionPicker,
        onSelect: handleMentionAutoComplete,
        ref: mentionPickerRef
      }) : null, shouldDisplayEmailBanner ? /*#__PURE__*/_jsx(BannerEmailRecipient, {
        ticketKey: ticketKey
      }) : null, /*#__PURE__*/_jsx("form", {
        children: /*#__PURE__*/_jsxs("div", {
          className: twMerge('relative flex min-h-16 w-full items-center gap-x-3 rounded-sm px-3 py-4', tabSelected === 'internal' ? 'bg-[#FCF2D8] dark:bg-[#332E1A]' : 'bg-gray-200', shouldDisplayEmailBanner ? 'rounded-t-none' : ''),
          children: [inputLockState.shouldShowLockIndicator && /*#__PURE__*/_jsx(InputLockIndicator, {
            showWhatsAppInactivityTemplateState: showWhatsAppInactivityTemplateState,
            assignee: assignee,
            ownExtensionKey: ownExtensionKey,
            onClickUnlock: onClickUnlockButton
          }), userAllowedToSendAttachments && !inputLockState.isLocked && channelSupportsAttachments && /*#__PURE__*/_jsx(AttachmentButton, {
            fileInputRef: fileInputRef,
            onAddAttachment: handleAddAttachment,
            disabled: attachmentButtonDisabled
          }), /*#__PURE__*/_jsx("textarea", {
            className: twMerge('block max-h-24 w-full resize-none overflow-y-scroll bg-transparent pt-1 text-sm text-wrap text-gray-800 antialiased placeholder:text-gray-500 focus:border-transparent focus:ring-0 focus:outline-hidden', inputLockState.additionalCssClass),
            ref: inputFieldRef,
            autoFocus: autoFocus,
            disabled: inputDisabled,
            onChange: handleTextChanged,
            onKeyDown: handleKeyDown,
            onClick: onClickTextField,
            value: activeMessageContent.text,
            "aria-label": activePlaceholder,
            placeholder: placeholder,
            onPaste: handlePasteImage,
            onDragOver: function onDragOver(event) {
              return event.preventDefault();
            },
            onDrop: function onDrop(event) {
              if (tabSelected === 'reply_external' && !allowInputInExternalReply) {
                event.preventDefault();
                event.stopPropagation();
              }
            }
          }), /*#__PURE__*/_jsx(Suspense, {
            fallback: /*#__PURE__*/_jsx(_Fragment, {}),
            children: /*#__PURE__*/_jsx(EmojiSearch, {
              textAreaRef: inputFieldRef,
              messageContent: activeMessageContent,
              updateMessage: function updateMessage(text, caretPosition) {
                return updateMessageAndCaretPosition(text, caretPosition, tabSelected);
              }
            })
          }), !inputLockState.isLocked && /*#__PURE__*/_jsx(EmojiPickerTickets, {
            textAreaRef: inputFieldRef,
            messageContent: activeMessageContent,
            updateMessage: function updateMessage(text, caretPosition) {
              return updateMessageAndCaretPosition(text, caretPosition, tabSelected);
            },
            disabled: tabSelected === 'reply_external' && !allowInputInExternalReply
          }), /*#__PURE__*/_jsx(ComposerLoadingPlaceholder, {
            loading: whatsAppEventsLoading
          })]
        })
      }), isEmailExternalReply && messageContentReplyExternal.emailSignature && /*#__PURE__*/_jsx(EmailSignaturePreview, {
        signature: messageContentReplyExternal.emailSignature,
        onRemove: handleRemoveSignature
      })]
    }), /*#__PURE__*/_jsx(InputFooterV2, {
      ticketKey: ticketKey,
      tabSelected: tabSelected,
      tabSelectedIsWritable: tabSelectedIsWritable,
      canReply: canReply,
      canAddInternalNote: canAddInternalNote,
      originPending: originPending,
      replyUnsupported: replyUnsupported,
      onClickTab: onClickTab,
      messageContent: activeMessageContent,
      unlockButtonClicked: unlockButtonClicked,
      sendMessage: sendMessage,
      setExternalChannelSelected: setExternalChannelSelected,
      onSelectPremadeAnswer: emailSignaturesEnabled ? handleSelectPremadeAnswer : undefined
    })]
  });
};
export default MessageInputTicketV2;